/**
 * 统一响应壳 `Result` —— 契约 §2。
 *
 * 全部对外 HTTP 一律套这个壳，没有例外(`/api/agent/run` 此前是唯一的例外，已改)。
 * ⚠ SSE 流**不套壳**，见 {@link file://./sse.ts}。
 */
import { z } from 'zod'
import { SUCCESS_CODE } from './error-codes'

/** 参数校验失败时的字段级错误。契约 §2.3。 */
export const fieldErrorSchema = z.looseObject({
  field: z.string(),
  reason: z.string(),
})
export type FieldError = z.infer<typeof fieldErrorSchema>

/**
 * 壳的【信封部分】，与 `data` 的形状无关。HTTP 层先拆信封判成败，再按端点 schema 解析 data。
 *
 * ⚠ 用 looseObject 而不是 object/strictObject：契约 §0 的字段纪律是
 *   「**可加**，不可删、不可改类型」—— 后端加字段是允许的，前端挡下来就是自己找事。
 *
 * 几个字段给了默认值，都是**有意的容错**：
 * - `message` 缺省为 null —— 它面向开发者，缺了不影响界面；
 * - `traceId` 缺省为空串 —— 真正权威的来源是 `x-trace-id` **响应头**(契约 §8.3 第 1 条)，
 *   SSE 流与网关 502 根本没有响应体，指望响应体拿 traceId 一定会落空。
 *   ⚠⚠ 而且它**会是 `null`**：2026-09-01 实测，无令牌调任何端点，网关鉴权关卡回的
 *   就是 `traceId: null`(见 test/real-responses.ts)。当成必填字符串会让「未登录」
 *   被误判成「非契约响应」，登录引导整条链路挂在这里，表现只是一句莫名的系统错误；
 * - `fieldErrors` 缺省为空数组 —— 空 ⇒ 按 code 出整体文案，非空 ⇒ 表单级高亮；
 * - `retryable` 缺省为 **false** —— 保守值。缺这一位时宁可不重试，也不要重试风暴。
 *   ⚠ 这不是假设：2026-09-01 实测，**运行中的后端一个响应都没有这个字段**
 *   (它是当天新增的，跑着的进程是改动前的编译)。
 */
export const resultEnvelopeSchema = z.looseObject({
  success: z.boolean(),
  code: z.string().min(1),
  // 面向开发者的文案，null 有意义(表示服务端没给)，所以保留 null 而不是归一成空串
  message: z.string().nullable().default(null),
  data: z.unknown(),
  // ⚠⚠ 用 preprocess 把 null 与缺失一起归一：这三个字段实测都会缺或为 null，
  //    只用 .default() 挡不住 null(default 只在 undefined 时生效)。
  traceId: z.preprocess((v) => v ?? '', z.string()),
  fieldErrors: z.preprocess((v) => v ?? [], z.array(fieldErrorSchema)),
  retryable: z.preprocess((v) => v ?? false, z.boolean()),
})
export type ResultEnvelope = z.infer<typeof resultEnvelopeSchema>

/** 成功壳。契约 §2：成功时 `code` 恒为 `OK`。 */
export function successResultSchema<T extends z.ZodType>(dataSchema: T) {
  return resultEnvelopeSchema.extend({
    success: z.literal(true),
    code: z.literal(SUCCESS_CODE),
    data: dataSchema,
  })
}

/** 失败壳。契约 §2：失败时 `data` 为 null。 */
export const failureResultSchema = resultEnvelopeSchema.extend({
  success: z.literal(false),
  data: z.null().default(null),
})
export type FailureResult = z.infer<typeof failureResultSchema>

/** 成败二选一的完整壳。给 mock 与端到端校验用；HTTP 层用信封版就够。 */
export function resultSchema<T extends z.ZodType>(dataSchema: T) {
  return z.union([successResultSchema(dataSchema), failureResultSchema])
}

/** `Result<Void>` 的 data —— A6 登出、A9 改密都是这种。 */
export const voidDataSchema = z.null().default(null)
